/**
 * «موجودی و اصلاحیه‌ها» (Phase 4): exact reference lookup (ADJ-/TD-/TW-/BE-/RC- or a ledger
 * transaction id — never fuzzy), every admin adjustment across customers, and the latest journal
 * transactions with their postings. Read-only: adjustments are made from the customer's Customer 360
 * («عملیات مالی → اصلاح موجودی»), where the target user and the before/after are explicit.
 */
import {
  AppContainer,
  Button,
  DataTable,
  formatAmount,
  Ltr,
  PageHeader,
  PageTabs,
  StateView,
  TextField,
} from '@hopex/ui';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api, dt, errorMessage, type Adjustment, type JournalTx } from '../../api.ts';
import { ADJ_OP, unitOf } from '../../lib/money-labels.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { TechCode } from '../../lib/ui.tsx';
import { JournalList } from './CustomerMoney.tsx';

interface Hit {
  kind: string;
  id: string;
  userId: string | null;
  source?: { type: string; id: string };
}
const KIND: Record<string, string> = {
  ADJ: 'اصلاحیه‌ی مدیر',
  TD: 'درخواست واریز تومان',
  TW: 'برداشت تومان',
  BE: 'شاهد بانکی',
  RC: 'پرونده‌ی تطبیق',
  LEDGER_TRANSACTION: 'سند دفتر کل',
};
const PAGE_OF: Record<string, string> = {
  TD: '/toman-deposits',
  TW: '/toman-withdrawals',
  BE: '/toman-deposits',
  RC: '/toman-deposits',
};

function Lookup() {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<{ hit: Hit | null; journal: JournalTx[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function search() {
    setBusy(true);
    setError(null);
    setRes(null);
    try {
      const { hit } = await api.get<{ hit: Hit | null }>(
        `/lookup?q=${encodeURIComponent(q.trim())}`,
      );
      const journal =
        hit?.kind === 'LEDGER_TRANSACTION'
          ? (await api.get<{ items: JournalTx[] }>(`/ledger/transactions?transactionId=${hit.id}`))
              .items
          : hit
            ? (await api.get<{ items: JournalTx[] }>(`/ledger/transactions?sourceId=${hit.id}`))
                .items
            : [];
      setRes({ hit, journal });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-line bg-surface-1 p-4"
      data-lookup
    >
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
      >
        <TextField
          label="جستجوی دقیق کد پیگیری یا شناسه‌ی سند"
          value={q}
          onChange={setQ}
          ltr
          placeholder="ADJ-00000001"
          className="min-w-64 flex-1"
        />
        <Button type="submit" loading={busy} isDisabled={q.trim().length < 3}>
          جستجو
        </Button>
      </form>
      {error && (
        <p role="alert" className="m-0 text-[13px] text-negative">
          {error}
        </p>
      )}
      {res &&
        (res.hit ? (
          <div className="flex flex-col gap-2" data-lookup-hit={res.hit.kind}>
            <p className="m-0 text-[13px]">
              {KIND[res.hit.kind] ?? res.hit.kind} · <TechCode>{res.hit.id}</TechCode>
            </p>
            <div className="flex flex-wrap gap-3 text-[13px]">
              {res.hit.userId && (
                <Link
                  to="/users/$id"
                  params={{ id: res.hit.userId }}
                  search={{ tab: res.hit.kind === 'ADJ' ? 'money-ops' : 'toman' }}
                  className="font-semibold text-accent-text hover:underline"
                >
                  پرونده‌ی مشتری
                </Link>
              )}
              {PAGE_OF[res.hit.kind] && (
                <Link
                  to={PAGE_OF[res.hit.kind] as string}
                  className="font-semibold text-accent-text hover:underline"
                >
                  صف مربوط
                </Link>
              )}
            </div>
            <JournalList rows={res.journal} />
          </div>
        ) : (
          <StateView compact kind="empty" title="موردی با این کد پیدا نشد" />
        ))}
    </section>
  );
}

function Adjustments() {
  const r = useRemote<{ items: Adjustment[] }>('/adjustments');
  return (
    <Remote r={r}>
      {(d) => (
        <DataTable<Adjustment>
          label="همه‌ی اصلاحیه‌ها"
          rows={d.items}
          density="compact"
          stickyFirst
          columns={[
            {
              id: 'ref',
              header: 'کد',
              isRowHeader: true,
              cell: (a) => <Ltr className="font-mono text-[12px]">{a.ref}</Ltr>,
            },
            {
              id: 'op',
              header: 'عملیات',
              cell: (a) => (
                <span className="flex flex-col">
                  <span>{ADJ_OP[a.operation] ?? a.operation}</span>
                  {a.reversedByAdjustmentId && (
                    <span className="text-[11px] text-negative">برگشت‌خورده</span>
                  )}
                </span>
              ),
            },
            {
              id: 'amount',
              header: 'مبلغ',
              numeric: true,
              cell: (a) => <Ltr className="hx-num">{formatAmount(a.amount, unitOf(a.asset))}</Ltr>,
            },
            {
              id: 'reason',
              header: 'دلیل / مرجع',
              cell: (a) => (
                <span className="flex flex-col">
                  <span>{a.reason}</span>
                  <TechCode>ref: {a.externalReference}</TechCode>
                </span>
              ),
            },
            { id: 'admin', header: 'مدیر', cell: (a) => <Ltr>{a.admin ?? '—'}</Ltr> },
            { id: 'at', header: 'زمان', cell: (a) => dt(a.createdAt) },
          ]}
          empty={<StateView compact kind="empty" title="اصلاحیه‌ای ثبت نشده است" />}
        />
      )}
    </Remote>
  );
}

function Journal() {
  const r = useRemote<{ items: JournalTx[] }>('/ledger/transactions');
  return <Remote r={r}>{(d) => <JournalList rows={d.items} />}</Remote>;
}

export function LedgerPage() {
  const [tab, setTab] = useState('adjustments');
  return (
    <AppContainer>
      <PageHeader title="موجودی و اصلاحیه‌ها" />
      <div className="mb-4">
        <Lookup />
      </div>
      <PageTabs
        label="دفتر کل"
        selected={tab}
        onSelect={setTab}
        items={[
          { id: 'adjustments', label: 'اصلاحیه‌های مدیر', content: <Adjustments /> },
          { id: 'journal', label: 'آخرین اسناد دفتر کل', content: <Journal /> },
        ]}
      />
      <p className="m-0 mt-4 text-[12px] leading-6 text-fg-subtle">
        اصلاح موجودی فقط از پرونده‌ی مشتری (عملیات مالی ← اصلاح موجودی) و با تأیید دومرحله‌ای مدیر
        انجام می‌شود. هیچ سندی ویرایش یا حذف نمی‌شود.
      </p>
    </AppContainer>
  );
}
